/**
 * 订单相关的 TanStack Query Hooks
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query'
import {
  createOrder,
  getOrderList,
  getOrderStatus,
  cancelOrder,
} from '@/services/orderService'
import type {
  Order,
  OrderRaw,
  OrderListParams,
  CreateOrderRequest,
  CreateOrderResponse,
  OrderStatusResponse,
} from '@/types/order'

/** 订单列表查询 Key */
const ORDERS_QUERY_KEY = ['orders'] as const

/** 订单状态查询 Key */
const ORDER_STATUS_QUERY_KEY = ['order-status'] as const

/**
 * 将分转换为元
 * @param fen 金额（分）
 * @returns 金额（元）
 */
function fenToYuan(fen: number): number {
  return Math.round(fen) / 100
}

/**
 * 转换订单原始数据为前端展示格式
 * @param raw 原始订单数据
 * @returns 转换后的订单数据
 */
function transformOrder(raw: OrderRaw): Order {
  const statusTextMap: Record<OrderRaw['status'], string> = {
    pending: '待支付',
    paid: '已支付',
    failed: '支付失败',
    cancelled: '已取消',
    refunded: '已退款',
    expired: '已过期',
  }

  const paymentMethodTextMap: Record<string, string> = {
    fyepay: '烽云支付',
  }

  const payTypeTextMap: Record<string, string> = {
    wechat: '微信支付',
    alipay: '支付宝',
    unionpay: '云闪付',
  }

  const paidAmountYuan = typeof raw.paidAmount === 'number' ? fenToYuan(raw.paidAmount) : null
  const baseCredits = typeof raw.credits === 'number' ? raw.credits : 0
  const bonusCredits = typeof raw.bonusCredits === 'number' ? raw.bonusCredits : 0
  const paymentMethod = raw.paymentMethod ?? null
  const payType = raw.payType ?? null

  return {
    id: raw.id,
    orderNo: raw.orderNo,
    packageId: raw.packageId,
    packageName: raw.packageName,
    packageType: raw.packageType,
    quantity: raw.quantity ?? 1,
    purchaseMonths: raw.purchaseMonths ?? 1,
    amountYuan: fenToYuan(raw.amount),
    paidAmountYuan,
    totalCredits: baseCredits + bonusCredits,
    baseCredits,
    bonusCredits,
    status: raw.status,
    statusText: statusTextMap[raw.status],
    paymentMethod,
    paymentMethodText:
      paymentMethod !== null
        ? paymentMethodTextMap[paymentMethod] ?? null
        : null,
    payType,
    payTypeText:
      payType !== null
        ? payTypeTextMap[payType] ?? null
        : null,
    paidAt: raw.paidAt ?? null,
    createdAt: raw.createdAt,
  }
}

/**
 * 转换订单列表
 * @param list 原始订单列表
 * @returns 转换后的订单列表
 */
function transformOrderList(list: OrderRaw[]): Order[] {
  return list.map(transformOrder)
}

/**
 * 获取订单列表的 Hook
 * @param params 查询参数
 * @returns 订单列表查询结果
 */
export function useOrders(
  params?: OrderListParams
): UseQueryResult<Order[]> {
  return useQuery({
    queryKey: [...ORDERS_QUERY_KEY, params] as const,
    queryFn: async (): Promise<Order[]> => {
      const data = await getOrderList(params)
      return transformOrderList(data.list)
    },
  })
}

/**
 * 订单列表分页响应
 */
export interface OrderListResult {
  orders: Order[]
  total: number
  page: number
  pageSize: number
}

/**
 * 获取分页订单列表的 Hook
 * @param params 查询参数
 * @returns 分页订单列表查询结果
 */
export function useOrdersPaginated(
  params?: OrderListParams
): UseQueryResult<OrderListResult> {
  return useQuery({
    queryKey: [...ORDERS_QUERY_KEY, 'paginated', params] as const,
    queryFn: async (): Promise<OrderListResult> => {
      const data = await getOrderList(params)
      return {
        orders: transformOrderList(data.list),
        total: data.total,
        page: data.page,
        pageSize: data.page_size,
      }
    },
  })
}

/**
 * 查询订单支付状态的 Hook
 * @param orderId 订单 ID
 * @param enabled 是否启用查询
 * @param refetchInterval 轮询间隔（毫秒）
 * @returns 订单状态查询结果
 */
export function useOrderStatus(
  orderId: string | null,
  enabled = true,
  refetchInterval: number | false = false
): UseQueryResult<OrderStatusResponse> {
  return useQuery({
    queryKey: [...ORDER_STATUS_QUERY_KEY, orderId] as const,
    queryFn: async (): Promise<OrderStatusResponse> => {
      if (orderId === null) {
        throw new Error('订单 ID 不能为空')
      }
      return await getOrderStatus(orderId)
    },
    enabled: enabled && orderId !== null,
    refetchInterval,
    // 禁用缓存，每次都获取最新状态
    staleTime: 0,
    gcTime: 0,
  })
}

/**
 * 创建订单 Mutation 结果类型
 */
export type CreateOrderMutationResult = UseMutationResult<
  CreateOrderResponse,
  Error,
  CreateOrderRequest
>

/**
 * 创建订单的 Hook
 * @returns 创建订单 mutation
 */
export function useCreateOrder(): CreateOrderMutationResult {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createOrder,
    onSuccess: (): void => {
      // 创建订单成功后，使订单列表缓存失效
      void queryClient.invalidateQueries({ queryKey: ORDERS_QUERY_KEY })
    },
  })
}

/**
 * 取消订单的 Hook
 * @returns 取消订单 mutation
 */
export function useCancelOrder(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: cancelOrder,
    onSuccess: (): void => {
      // 取消订单成功后，使订单列表缓存失效
      void queryClient.invalidateQueries({ queryKey: ORDERS_QUERY_KEY })
    },
  })
}

export { ORDERS_QUERY_KEY, ORDER_STATUS_QUERY_KEY }
